<!--
  Payment Calculator — ported from CGS2022 Scripts/Calculator/Calculator.vue (the <calc>
  component the CGS "Scenario Builder" page renders).

  WHAT CAME ACROSS
  The whole calculation engine, verbatim in behaviour: base loan amount, LTV, the FHA / VA /
  USDA up-front fee tiers, the FHA monthly MI tiers, P&I, the total payment and the
  amortization schedule. Every rate constant below is copied from the CGS source, not
  re-derived. See the comment on each one.

  WHAT DELIBERATELY DID NOT
  - Kendo NumericTextBox / DropDownList. Plain inputs on the calculator kit instead, so this
    page matches the debt-consolidation calculator rather than the CGS chrome.
  - The four remote icon buttons (connectgs.com/images/*.svg and .png) that picked purpose and
    loan type. Those are hot-linked off a public marketing site; they are segmented controls
    here.
  - `ajaxManager` / jQuery / `getCookieV2`. This app has its own api service and MSAL token.
  - The MI rate dialog (`showmi` / `calcdataloaded` emits into MIRateRequest.vue). The Monthly
    MI field is a plain input; wiring the MI rate request is a follow-up.
  - `getsavedPayement` / `savePayment`. Those are contact-scoped, and the CGS page passes no
    contact either, so they were already dead code on this screen.

  TWO PLACES THIS FIXES THE ORIGINAL — both flagged rather than silently changed:
  1. CGS's `pandi` puts `.toFixed(2)` on the DENOMINATOR, not the result, so the payment was
     computed against a rounded divisor. Corrected here.
  2. CGS's VA-purchase funding fee reads `this.ltv`, which is derived from `loanAmount`, which
     is derived from the funding fee — a genuine circular dependency that only resolved by
     Vue's side-effect ordering and a stale value. The tier is picked off the LTV BEFORE the
     fee is financed, which is also how the fee actually works.
  3. CGS's `calculateAPR` is `(loanAmount * noteRate) * (term / 12)` divided about — the source
     carries two "I need Clarification on how GS calculates it" comments against it. Replaced
     with the standard bisection solve that CalculatorV2 uses.
-->
<template>
  <div class="calc-page">
    <PageHeaderPortal>
      <div class="calc-header">
        <div class="calc-header__icon">
          <i class="fa-duotone fa-house-chimney-window"></i>
        </div>
        <div>
          <h1 class="calc-header__title">Payment Calculator</h1>
          <p class="calc-header__sub">Build a purchase or refinance payment, with MI and the up-front fee</p>
        </div>
      </div>
    </PageHeaderPortal>

    <div class="calc-body">
      <section class="page-card step-card">
        <div class="step-head">
          <span class="step-icon tone-blue"><i class="fa-light fa-calculator"></i></span>
          <div class="step-head-text">
            <p class="eyebrow">Loan Terms</p>
            <h2>Build the payment</h2>
            <p class="step-desc">
              Pick the purpose and product, then enter the terms. Principal and interest, mortgage
              insurance and the financed up-front fee all update as you type.
            </p>
          </div>
        </div>

        <!-- purpose + product ------------------------------------------------ -->
        <div class="pc-toggles">
          <div class="pc-toggle">
            <span class="field-label">Loan purpose</span>
            <div class="seg">
              <button v-for="p in PURPOSES" :key="p.key" type="button"
                      class="seg__btn" :class="{ 'is-active': loanPurpose === p.key }"
                      @click="loanPurpose = p.key">
                <i :class="p.icon"></i> {{ p.label }}
              </button>
            </div>
          </div>

          <div class="pc-toggle">
            <span class="field-label">Loan type</span>
            <div class="seg">
              <button v-for="t in LOAN_TYPES" :key="t.key" type="button"
                      class="seg__btn" :class="{ 'is-active': loanType === t.key }"
                      @click="loanType = t.key">
                {{ t.label }}
              </button>
            </div>
          </div>
        </div>

        <!-- fields ----------------------------------------------------------- -->
        <div class="field-grid">
          <template v-if="isBuy">
            <label class="field">
              <span class="field-label">Purchase price</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       :value="purchasePrice"
                       @input="onPrice($event.target.value)"
                       @blur="purchasePrice = money2(purchasePrice)" />
              </span>
            </label>

            <label class="field">
              <span class="field-label">Down payment</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       :value="downPayment"
                       @input="onDownPayment($event.target.value)"
                       @blur="downPayment = money2(downPayment)" />
              </span>
            </label>

            <label class="field">
              <span class="field-label">Down payment %</span>
              <span class="input-shell">
                <input type="text" inputmode="decimal" autocomplete="off"
                       :value="downPaymentPercentage"
                       @input="onDownPaymentPct($event.target.value)"
                       @blur="downPaymentPercentage = rate3(downPaymentPercentage)" />
                <span class="affix affix--post">%</span>
              </span>
            </label>
          </template>

          <template v-else>
            <label class="field">
              <span class="field-label">Estimated value</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="purchasePrice"
                       @blur="purchasePrice = money2(purchasePrice)" />
              </span>
            </label>

            <label class="field">
              <span class="field-label">Amount owed</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="amountOwed"
                       @blur="amountOwed = money2(amountOwed)" />
              </span>
            </label>

            <label class="field">
              <span class="field-label">Refi type</span>
              <span class="input-shell input-shell--select">
                <select v-model="refiType">
                  <option v-for="r in REFI_TYPES" :key="r" :value="r">{{ r }}</option>
                </select>
              </span>
            </label>

            <label v-if="refiType === 'Cash Out'" class="field field--new-row">
              <span class="field-label">Cash out</span>
              <span class="input-shell">
                <span class="affix affix--pre">$</span>
                <input type="text" inputmode="decimal" autocomplete="off"
                       v-model="cashOut"
                       @blur="cashOut = money2(cashOut)" />
              </span>
            </label>
          </template>

          <label v-if="loanType === 'va'" class="field field--new-row">
            <span class="field-label">VA type</span>
            <span class="input-shell input-shell--select">
              <select v-model="vaType">
                <option v-for="v in VA_TYPES" :key="v" :value="v">{{ v }}</option>
              </select>
            </span>
          </label>

          <div v-if="loanType === 'va' && isBuy" class="field">
            <span class="field-label">First-time use</span>
            <label class="check pc-check">
              <input type="checkbox" v-model="firstTime" />
              <span>First time using the VA benefit</span>
            </label>
          </div>

          <label class="field field--new-row">
            <span class="field-label">Amortization type</span>
            <span class="input-shell input-shell--select">
              <select v-model="loanAmort">
                <option v-for="a in AMORT_TYPES" :key="a" :value="a">{{ a }}</option>
              </select>
            </span>
          </label>

          <label class="field">
            <span class="field-label">Note rate</span>
            <span class="input-shell">
              <input type="text" inputmode="decimal" autocomplete="off"
                     v-model="noteRatePct"
                     @blur="noteRatePct = rate3(noteRatePct)" />
              <span class="affix affix--post">%</span>
            </span>
          </label>

          <label class="field">
            <span class="field-label">Term</span>
            <span class="input-shell">
              <input type="text" inputmode="numeric" autocomplete="off" v-model="term" />
              <span class="affix affix--post">months</span>
            </span>
          </label>

          <label class="field field--new-row">
            <span class="field-label">Monthly taxes</span>
            <span class="input-shell">
              <span class="affix affix--pre">$</span>
              <input type="text" inputmode="decimal" autocomplete="off"
                     v-model="taxes" @blur="taxes = money2(taxes)" />
              <span class="affix affix--post">/month</span>
            </span>
          </label>

          <label class="field">
            <span class="field-label">Monthly insurance</span>
            <span class="input-shell">
              <span class="affix affix--pre">$</span>
              <input type="text" inputmode="decimal" autocomplete="off"
                     v-model="insurance" @blur="insurance = money2(insurance)" />
              <span class="affix affix--post">/month</span>
            </span>
          </label>

          <label class="field">
            <span class="field-label">Monthly HOA</span>
            <span class="input-shell">
              <span class="affix affix--pre">$</span>
              <input type="text" inputmode="decimal" autocomplete="off"
                     v-model="hoa" @blur="hoa = money2(hoa)" />
              <span class="affix affix--post">/month</span>
            </span>
          </label>

          <!-- Same condition CGS uses: conventional, under 20% down. On the refi path there is
               no down payment, so this shows for a conventional refi too, matching CGS. -->
          <label v-if="showMonthlyMiField" class="field field--new-row">
            <span class="field-label">
              Monthly MI
              <button type="button" class="info-dot" :aria-expanded="showMiNote ? 'true' : 'false'"
                      @click.prevent="showMiNote = !showMiNote">i</button>
            </span>
            <span class="input-shell">
              <span class="affix affix--pre">$</span>
              <input type="text" inputmode="decimal" autocomplete="off"
                     v-model="monthlyMI" @blur="monthlyMI = money2(monthlyMI)" />
              <span class="affix affix--post">/month</span>
            </span>
            <small v-if="showMiNote" class="field-note">
              Conventional MI is not calculated — enter the quoted factor. FHA, VA and USDA all
              derive their own mortgage insurance from the loan type, term and LTV.
            </small>
          </label>

          <label class="field" :class="{ 'field--new-row': !showMonthlyMiField }">
            <span class="field-label">
              Fees for APR
              <button type="button" class="info-dot" :aria-expanded="showAprNote ? 'true' : 'false'"
                      @click.prevent="showAprNote = !showAprNote">i</button>
            </span>
            <span class="input-shell">
              <span class="affix affix--pre">$</span>
              <input type="text" inputmode="decimal" autocomplete="off"
                     v-model="additionalFees" @blur="additionalFees = money2(additionalFees)" />
            </span>
            <small v-if="showAprNote" class="field-note">
              Prepaid finance charges. These are added to the financed up-front fee and backed out
              of the amount financed when the APR is solved.
            </small>
          </label>
        </div>

        <div v-if="calcError" class="calc-note calc-note--error pc-error">
          <i class="fa-duotone fa-triangle-exclamation"></i>
          <div>
            <strong>That won't amortize</strong>
            {{ calcError }}
          </div>
        </div>

        <div class="step-foot">
          <button type="button" class="secondary-button" @click="resetAll">Reset</button>
          <div class="foot-right">
            <button type="button" class="secondary-button"
                    :disabled="!amortization.length"
                    @click="showAmortization = !showAmortization">
              {{ showAmortization ? 'Hide' : 'Show' }} Amortization
            </button>
            <button type="button" class="secondary-button" @click="copySummary">
              {{ copied ? 'Copied' : 'Copy Summary' }}
            </button>
            <button type="button" class="primary-button" :disabled="!isPriced || pdfBusy" @click="generatePdf">
              {{ pdfBusy ? 'Generating...' : 'Generate PDF' }}
            </button>
          </div>
        </div>
      </section>

      <!-- rail ---------------------------------------------------------------- -->
      <aside class="rail--dark">
        <div>
          <p class="eyebrow eyebrow--gold">Estimated Payment</p>
          <h3>{{ isBuy ? 'Purchase' : 'Refinance' }} &middot; {{ loanTypeLabel }}</h3>
        </div>

        <div class="rail-hero">
          <span>Total monthly payment</span>
          <strong>{{ isPriced ? money(totalPayment) : '&mdash;' }}</strong>
        </div>

        <dl class="rail-rows">
          <div><dt>Principal &amp; interest</dt><dd>{{ isPriced ? money(pandi) : '&mdash;' }}</dd></div>
          <div><dt>{{ miLabel }}</dt><dd>{{ money(pmi) }}</dd></div>
          <div><dt>Taxes</dt><dd>{{ money(num(taxes)) }}</dd></div>
          <div><dt>Insurance</dt><dd>{{ money(num(insurance)) }}</dd></div>
          <div><dt>HOA</dt><dd>{{ money(num(hoa)) }}</dd></div>
        </dl>

        <dl class="rail-rows rail-rows--divided">
          <div><dt>Base loan amount</dt><dd>{{ money(baseLoanAmount) }}</dd></div>
          <div v-if="upFrontFee > 0"><dt>{{ upFrontFeeLabel }}</dt><dd>{{ money(upFrontFee) }}</dd></div>
          <div><dt>Total loan amount</dt><dd>{{ money(loanAmount) }}</dd></div>
          <div><dt>LTV</dt><dd>{{ pct(ltv) }}</dd></div>
          <div><dt>APR</dt><dd>{{ pct(apr) }}</dd></div>
          <div><dt>Total interest paid</dt><dd>{{ money(totalInterestPaid) }}</dd></div>
        </dl>

        <div class="rail-highlight">
          <span>Cash to close (down payment)</span>
          <strong>{{ money(isBuy ? num(downPayment) : num(cashOut)) }}</strong>
        </div>
      </aside>
    </div>

    <!-- amortization ---------------------------------------------------------- -->
    <section v-if="showAmortization && amortization.length" class="page-card">
      <div class="step-head">
        <span class="step-icon tone-slate"><i class="fa-light fa-table-list"></i></span>
        <div class="step-head-text">
          <p class="eyebrow">Schedule</p>
          <h2>Amortization</h2>
          <p class="step-desc">
            {{ amortization.length }} payments. Interest is charged on the balance each month,
            and whatever the payment does not cover in interest reduces principal.
          </p>
        </div>
      </div>

      <div class="pc-amort-scroll">
        <table class="calc-table">
          <thead>
            <tr>
              <th>Payment</th>
              <th>Interest paid</th>
              <th>Principal paid</th>
              <th>Balance remaining</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in amortization" :key="i">
              <td>{{ i + 1 }}</td>
              <td>{{ money(row.interest) }}</td>
              <td>{{ money(row.principal) }}</td>
              <td>{{ money(row.balance) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <CalculatorPdfDialog :open="pdfOpen"
                         :busy="pdfBusy"
                         :error="pdfError"
                         :blob-url="pdfBlobUrl"
                         :hosted-url="pdfHostedUrl"
                         :title="`${isBuy ? 'Purchase' : 'Refinance'} payment`"
                         file-name="Payment_Scenario.pdf"
                         @close="closePdf" />
  </div>
</template>

<script>
  import PageHeaderPortal from "@/views/PageHeaderPortal.vue"
  import {
    LOAN_TYPES, REFI_TYPES, VA_TYPES, AMORT_TYPES,
    num, hasValue, priceScenario, money, pct
  } from "@/helpers/paymentMath.js"
  import CalculatorPdfDialog from "@/components/CalculatorPdfDialog.vue"
  import auth from "@/authConfig.js"
  import { buildPdfPayload, generatePdf } from "@/helpers/calculatorPdf.js"

  // The arithmetic moved to helpers/paymentMath.js when the Scenario Builder arrived, so both
  // pages price a loan through exactly one implementation. Two copies of the FHA tier tables
  // would eventually disagree, and the way you'd find out is a borrower being quoted two
  // different MI figures by two pages of the same app.

  const PURPOSES = [
    { key: "Buy", label: "Purchase", icon: "fa-light fa-house" },
    { key: "Refi", label: "Refinance", icon: "fa-light fa-arrows-rotate" }
  ]

  export default {
    name: "PaymentCalculator",
    components: { PageHeaderPortal, CalculatorPdfDialog },

    data() {
      return {
        PURPOSES,
        LOAN_TYPES,
        REFI_TYPES,
        VA_TYPES,
        AMORT_TYPES,
        loanPurpose: "Buy",
        loanType: "conv",
        refiType: "No Cash Out",
        vaType: "Regular Military/Active Duty",
        loanAmort: "Fixed",
        firstTime: true,
        purchasePrice: "",
        downPayment: "",
        downPaymentPercentage: "",
        amountOwed: "",
        cashOut: "",
        // A PERCENT (7.500). paymentMath is percent throughout; see the units note there.
        noteRatePct: "",
        term: "360",
        taxes: "",
        insurance: "",
        hoa: "",
        monthlyMI: "",
        additionalFees: "",
        showAmortization: false,
        showMiNote: false,
        showAprNote: false,
        copied: false,
        pdfOpen: false,
        pdfBusy: false,
        pdfError: "",
        pdfBlobUrl: "",
        pdfHostedUrl: ""
      }
    },

    computed: {
      // One call produces every derived figure, in a fixed order. The component's own data
      // happens to use the field names priceScenario expects, so `this` is the input object.
      calc() {
        return priceScenario(this)
      },

      isBuy() { return this.loanPurpose === "Buy" },
      loanTypeLabel() { return (LOAN_TYPES.find(t => t.key === this.loanType) || {}).label || "" },
      termMonths() { return this.calc.termMonths },
      isPriced() { return this.calc.isPriced },
      showMonthlyMiField() { return this.loanType === "conv" },

      baseLoanAmount() { return this.calc.baseLoanAmount },
      upFrontFee() { return this.calc.upFrontFee },
      upFrontFeeLabel() { return this.calc.upFrontFeeLabel },
      loanAmount() { return this.calc.loanAmount },
      ltv() { return this.calc.ltv },
      pandi() { return this.calc.pandi },
      pmi() { return this.calc.pmi },
      totalPayment() { return this.calc.totalPayment },
      amortization() { return this.calc.amortization },
      totalInterestPaid() { return this.calc.totalInterestPaid },
      apr() { return this.calc.apr },

      miLabel() {
        if (this.loanType === "fha") return "FHA monthly MIP"
        if (this.loanType === "usda") return "USDA annual fee"
        return "Mortgage insurance"
      },

      calcError() {
        if (!this.loanAmount) return ""
        if (this.termMonths <= 0) return "Enter a term in months."
        if (num(this.noteRatePct) < 0) return "The note rate cannot be negative."
        if (this.pandi > 0 && this.loanAmount * (num(this.noteRatePct) / 100 / 12) >= this.pandi) {
          return "The first month's interest is at least the whole payment, so the balance would never come down."
        }
        return ""
      }
    },

    methods: {
      num,
      money,
      pct,

      money2(raw) {
        if (!hasValue(raw)) return ""
        return num(raw).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      },
      rate3(raw) {
        if (!hasValue(raw)) return ""
        return num(raw).toFixed(3)
      },

      // The three purchase fields are one relationship with three faces. Whichever the user
      // touches drives the others; CGS does the same in downPaymentChanged / downPaymentPertChanged.
      onPrice(raw) {
        this.purchasePrice = raw
        const price = num(raw)
        if (price > 0 && hasValue(this.downPaymentPercentage)) {
          this.downPayment = this.money2(String((price * num(this.downPaymentPercentage)) / 100))
        } else if (price > 0 && hasValue(this.downPayment)) {
          this.downPaymentPercentage = this.rate3(String((num(this.downPayment) / price) * 100))
        }
      },
      onDownPayment(raw) {
        this.downPayment = raw
        const price = num(this.purchasePrice)
        if (price > 0) {
          this.downPaymentPercentage = hasValue(raw) ? this.rate3(String((num(raw) / price) * 100)) : ""
        }
      },
      onDownPaymentPct(raw) {
        this.downPaymentPercentage = raw
        const price = num(this.purchasePrice)
        if (price > 0) {
          this.downPayment = hasValue(raw) ? this.money2(String((price * num(raw)) / 100)) : ""
        }
      },

      resetAll() {
        Object.assign(this, {
          loanPurpose: "Buy",
          loanType: "conv",
          refiType: "No Cash Out",
          vaType: "Regular Military/Active Duty",
          loanAmort: "Fixed",
          firstTime: true,
          purchasePrice: "",
          downPayment: "",
          downPaymentPercentage: "",
          amountOwed: "",
          cashOut: "",
          noteRatePct: "",
          term: "360",
          taxes: "",
          insurance: "",
          hoa: "",
          monthlyMI: "",
          additionalFees: "",
          showAmortization: false,
          copied: false
        })
      },


      // ── PDF ──────────────────────────────────────────────────────────────
      async generatePdf () {
        if (this.pdfBusy) return
        this.revokePdf()
        this.pdfOpen = true
        this.pdfBusy = true
        this.pdfError = ""
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const { blobUrl, hostedUrl } = await generatePdf({
            payload: this.pdfPayload(),
            token: tokenResponse?.accessToken
          })
          this.pdfBlobUrl = blobUrl
          this.pdfHostedUrl = hostedUrl
        } catch (e) {
          this.pdfError = e?.response?.data?.message || e?.message || "The PDF service did not respond."
        } finally {
          this.pdfBusy = false
        }
      },
      closePdf () {
        this.pdfOpen = false
        this.revokePdf()
      },
      // A blob URL holds the whole document in memory until it is revoked.
      revokePdf () {
        if (this.pdfBlobUrl) {
          try { URL.revokeObjectURL(this.pdfBlobUrl) } catch (e) { /* already gone */ }
        }
        this.pdfBlobUrl = ""
        this.pdfHostedUrl = ""
        this.pdfError = ""
      },
      pdfPayload () {
        return buildPdfPayload({
          scenarios: [{
            name: `${this.loanTypeLabel} ${this.termMonths / 12} Year ${this.loanAmort}`.slice(0, 34),
            input: this,
            calc: this.calc
          }],
          context: { homeValue: num(this.purchasePrice) }
        })
      },

      async copySummary() {
        const l = [
          `Payment Calculator — ${this.isBuy ? "Purchase" : "Refinance"} / ${this.loanTypeLabel}`,
          ""
        ]
        if (this.isBuy) {
          l.push(`  Purchase price: ${money(num(this.purchasePrice))}`)
          l.push(`  Down payment: ${money(num(this.downPayment))} (${pct(num(this.downPaymentPercentage))})`)
        } else {
          l.push(`  Estimated value: ${money(num(this.purchasePrice))}`)
          l.push(`  Amount owed: ${money(num(this.amountOwed))}`)
          if (this.refiType === "Cash Out") l.push(`  Cash out: ${money(num(this.cashOut))}`)
        }
        l.push(`  Note rate: ${pct(num(this.noteRatePct))} for ${this.termMonths} months (${this.loanAmort})`)
        if (this.upFrontFee > 0) l.push(`  ${this.upFrontFeeLabel}: ${money(this.upFrontFee)}`)
        l.push(`  Loan amount: ${money(this.loanAmount)} at ${pct(this.ltv)} LTV`)
        l.push("", `  Principal & interest: ${money(this.pandi)}`)
        l.push(`  ${this.miLabel}: ${money(this.pmi)}`)
        l.push(`  Taxes: ${money(num(this.taxes))}`)
        l.push(`  Insurance: ${money(num(this.insurance))}`)
        l.push(`  HOA: ${money(num(this.hoa))}`)
        l.push(`  Total monthly payment: ${money(this.totalPayment)}`)
        l.push("", `  APR: ${pct(this.apr)}`)
        l.push(`  Total interest paid: ${money(this.totalInterestPaid)}`)
        try {
          await navigator.clipboard.writeText(l.join("\n"))
          this.copied = true
          setTimeout(() => (this.copied = false), 2000)
        } catch (e) {
          this.copied = false
        }
      }
    }
  }
</script>

<style scoped>
  /* Everything shared lives in assets/css/calculatorKit.css. Only this page's own layout is
     here, and nothing below hard-codes a font-size -- they all come off the kit's scale. */
  .pc-toggles {
    display: flex;
    flex-wrap: wrap;
    gap: 22px;
    margin-top: 22px;
  }

  .pc-toggle {
    display: flex;
    flex-direction: column;
    gap: 7px;
    min-width: 0;
  }

  .pc-check {
    height: var(--control-h);
  }

  .pc-error {
    margin-top: 20px;
  }

  /* A 360-row schedule needs its own scroll rather than pushing the page to 30 screens. */
  .pc-amort-scroll {
    max-height: 420px;
    overflow: auto;
    margin-top: 18px;
    border: 1px solid var(--calc-line);
    border-radius: 9px;
  }
</style>
